import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

// Use unified protocol endpoint
const BASE_URL = '/api/preop-protocols';
const LEGACY_URL = '/api/protocols'; // For backward compatibility

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('_at') || localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

// Unified protocol functions
export async function getProtocols(options = {}) {
  try {
    const params = new URLSearchParams();
    if (options.category) params.append('category', options.category);
    if (options.status) params.append('status', options.status);
    if (options.facilityId) params.append('facilityId', options.facilityId);
    if (options.includeDisabled) params.append('includeDisabled', 'true');

    const queryString = params.toString();
    const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

    const { data } = await axios.get(getApiUrl(url), { headers: getAuthHeaders() });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch protocols');
  }
}

export async function createProtocol(protocol) {
  try {
    const { data } = await axios.post(getApiUrl(BASE_URL), protocol, { headers: getAuthHeaders() });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create protocol');
  }
}

export async function updateProtocol(id, protocol) {
  try {
    const { data } = await axios.put(getApiUrl(`${BASE_URL}/${id}`), protocol, {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update protocol');
  }
}

export async function deleteProtocol(id) {
  try {
    const { data } = await axios.delete(getApiUrl(`${BASE_URL}/${id}`), {
      headers: getAuthHeaders(),
    });
    return data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete protocol');
  }
}

// ASA Preop Protocol functions
export const protocolsApi = {
  // Get all ASA preop protocols
  getAllProtocols: async () => {
    try {
      const response = await axios.get(getApiUrl('/api/preop-protocols'), {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      // Don't log 401 errors - they're expected when not authenticated
      if (error.response?.status !== 401) {
        console.error('Error fetching ASA protocols:', error);
      }
      throw error;
    }
  },

  // Get a single protocol by ID
  getProtocolById: async id => {
    try {
      const response = await axios.get(getApiUrl(`/api/preop-protocols/protocols/${id}`), {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching protocol:', error);
      throw error;
    }
  },

  // Create a new ASA preop protocol
  createProtocol: async protocol => {
    try {
      const response = await axios.post(getApiUrl('/api/preop-protocols'), protocol, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating protocol:', error);
      throw error;
    }
  },

  // Update an existing protocol
  updateProtocol: async (id, protocol) => {
    try {
      const response = await axios.put(
        getApiUrl(`/api/preop-protocols/protocols/${id}`),
        protocol,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating protocol:', error);
      throw error;
    }
  },

  // Delete a protocol
  deleteProtocol: async id => {
    try {
      const response = await axios.delete(getApiUrl(`/api/preop-protocols/protocols/${id}`), {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting protocol:', error);
      throw error;
    }
  },

  // Toggle protocol enabled/disabled status
  toggleProtocol: async (id, enabled) => {
    try {
      const response = await axios.patch(
        getApiUrl(`/api/preop-protocols/protocols/${id}/toggle`),
        { enabled },
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling protocol:', error);
      throw error;
    }
  },

  // Get protocol history
  getProtocolHistory: async id => {
    try {
      const response = await axios.get(getApiUrl(`/api/preop-protocols/protocols/${id}/history`), {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching protocol history:', error);
      throw error;
    }
  },

  // Create surgeon preference
  createSurgeonPreference: async (protocolId, preference) => {
    try {
      const response = await axios.post(
        getApiUrl(`/api/preop-protocols/${protocolId}/surgeon-preferences`),
        preference,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating surgeon preference:', error);
      throw error;
    }
  },

  // Create facility override
  createFacilityOverride: async (protocolId, override) => {
    try {
      const response = await axios.post(
        getApiUrl(`/api/preop-protocols/${protocolId}/facility-override`),
        override,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating facility override:', error);
      throw error;
    }
  },

  // Get standalone configuration
  getStandaloneConfig: async () => {
    try {
      const response = await axios.get(getApiUrl('/api/preop-protocols/standalone/config'), {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching standalone config:', error);
      throw error;
    }
  },

  // Save standalone configuration
  saveStandaloneConfig: async config => {
    try {
      const response = await axios.post(
        getApiUrl('/api/preop-protocols/standalone/config'),
        config,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving standalone config:', error);
      throw error;
    }
  },

  // Get facility overrides for a protocol
  getFacilityOverrides: async (protocolId, facilityId) => {
    try {
      const response = await axios.get(
        getApiUrl(`/api/preop-protocols/${protocolId}/facility/${facilityId}`),
        {
          headers: getAuthHeaders(),
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching facility overrides:', error);
      throw error;
    }
  },

  // Save facility overrides for a protocol
  saveFacilityOverrides: async (protocolId, facilityId, overrides) => {
    try {
      const response = await axios.post(
        getApiUrl(`/api/preop-protocols/${protocolId}/facility/${facilityId}`),
        overrides,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving facility overrides:', error);
      throw error;
    }
  },
};
